/**
 * The ranked-breakdown endpoint (VERA-260) — the analytics page's first TABLE card.
 *
 * ## Why this is an axios service and not a redux-saga action, unlike its siblings
 *
 * Every other analytics request on this page goes through `store/analytics` — an action,
 * a saga, a reducer slot and a `useQuerySelect` wrapper. That machinery exists for a
 * reason the leaderboard does not share: those responses are read by SEVERAL cards at
 * once (the totals feed four KPI tiles and the ROI hero), so a shared redux slot is what
 * stops four components issuing the same request.
 *
 * A leaderboard is read by exactly one card, and its parameters include that card's own
 * dimension — so a shared slot would have to be keyed per request anyway, which is what
 * `useAnalyticsScansMap` had to retrofit (`AnalyticsScansMapCache`) after the single-slot
 * version handed every map card whichever response arrived last. react-query keys by
 * request natively, so the retrofit is the default here.
 *
 * Types are declared against `@repo/types` — the same interfaces the API returns — rather
 * than restated, so the wire contract has one definition (unlike `askStatsApi`, which
 * duplicates because the platform does not depend on that package; it does depend on this
 * one).
 */
import type {
  ITopInfluencers,
  TopInfluencerDimension,
  TopInfluencerRankKey,
} from '@repo/types'

import { createApiCall } from './index'

export interface TopInfluencersParams {
  /** ISO strings — the same wire form every analytics request uses. */
  from: string
  to: string
  dimension: TopInfluencerDimension
  /**
   * Which column to rank AND truncate on. Omitted means the server's default (`scans`).
   *
   * Sent as a query parameter rather than re-sorted here on purpose — see
   * `TopInfluencerRankKey`'s own docblock. The response is the top `limit` rows ON THIS
   * KEY, so an owner outside a scans-ranked top ten is simply absent from the payload
   * and no client-side ordering can bring them back.
   */
  rank_by?: TopInfluencerRankKey
  limit?: number
  owner_ids?: string[]
  product_ids?: string[]
  certificate_ids?: string[]
  certificate_statuses?: string[]
  scan_statuses?: string[]
  location?: string[]
  /** `lat,lng;lat,lng;…` — the drawn ring, already encoded. */
  area?: string
}

/**
 * ⚠️ ARRAYS GO THROUGH AXIOS' `params`, NOT A HAND-BUILT QUERY STRING.
 *
 * This was hand-rolled with `URLSearchParams` and REPEATED bare keys
 * (`location=US&location=GB`), on the reasoning that Nest's `@IsArray()` reads repeated
 * keys. Driven against the running API, that is wrong in the one case that matters most:
 *
 *     location[]=United%20States   -> ['United States']   200
 *     location=United%20States     -> 'United States'     400  "location must be an array"
 *
 * Express parses the query with `qs`, so `key[]=` is an array at ANY length while a
 * repeated bare key is a SCALAR when it appears once and an array only from two
 * occurrences on. So the hand-rolled form worked for a two-value filter and 400'd for a
 * one-value filter — the more common selection, and a failure the empty card would have
 * shown as "no data" rather than as an error.
 *
 * Axios' default serialiser already emits `key[]=`, which is what every other analytics
 * request on this page has always sent (the sagas pass `params: payload` straight
 * through). Handing it the object is therefore both the correct wire form and the
 * established one — the hand-rolled serialiser was a second mechanism for a solved
 * problem, which is what §28 is about.
 *
 * `undefined` values are dropped by axios, and an empty array is dropped here, so
 * "no filter" and "omit the parameter" stay the same request — which is what the
 * server's `?.length` checks expect.
 */
const toRequestParams = (params: TopInfluencersParams): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined && value !== null && (!Array.isArray(value) || value.length > 0),
    ),
  )

export const analyticsTopInfluencersApi = {
  get: async (params: TopInfluencersParams): Promise<ITopInfluencers> => {
    const { data } = await createApiCall({
      method: 'GET',
      url: '/analytics/admin/top-influencers',
      params: toRequestParams(params),
    })
    return data.data
  },
}
